import type {
    PlayerInstance,
} from '@src/framework/editor/src/page/index/components/canvas/element-types/image-element/media-container/wcjs-player-instance';
import type AnimationModel from '@src/slide/domain/ui-model/element-modeles/anim-model';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { SuperPlayer } from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';
import { PlayerType } from '../note-view-media-sync-manager/core/note-view-media-sync-manager.interface';

export enum SelectedGifHandler {
    'ADD' = 'add',
    'DELETE' = 'delete',
}

export type PreservedAudio = {
    audioIns: HTMLAudioElement;
    stopAtSlideIndex: number;
    startAtSlideIndex: number;
};

/**
 * 标记音视频元素是来自哪个功能模块
 */
export enum MediaRegion {
    EDITOR = 'editor',
    PLAYER = 'player',
    MOBILE_PREVIEW = 'mobilePreview'
}

export type WebVideoPlayer = SuperPlayer & {
    playerType: PlayerType
};

export type VideoType = WebVideoPlayer | PlayerInstance;

export interface IMediaManager {
    /**
     * 移动端预览/编辑态切换
     */
    isEditMode: boolean;
    elementKey: string | null;

    /* --- 视频 --- */
    // 编辑态下的videoInstances
    videoInstances: Map<string, VideoType | null>;

    // 演示态下的videoInstancesInPlayer
    videoInstancesInPlayer: Map<string, VideoType | null>;

    isVideoFullScreen: boolean;

    /* --- 音频 --- */
    // 音频状态
    audioInstances: Map<string, HTMLAudioElement>;

    audioInstancesInPlayer: Map<string, HTMLAudioElement>;

    /* --- iframe --- */
    // iframe 状态
    prevIframeSelected: boolean;

    currentIframeSelected: boolean;

    // iframe 封面
    iframePoster: string | null;

    iframePosterCaches: Map<string, string>;

    /* --- gif --- */
    // 正在播放动画的 gif 元素 key
    animatingGif: Set<string>;

    /* ================================ 演示态数据 ================================ */
    // 演示态下已经完成播放动画的 gif 元素 key
    playerAnimationFinishedGif: Set<string>;

    playerAnimatingGif: Set<string>;

    thumbnailElementKey: string;

    isShowIframe: boolean;

    fetchIframePoster: any;

    notifyGifScaleUpdate: any;

    shouldRenderGifPoster(key: string): boolean;

    shouldRenderDomGif(key: string): boolean;

    shouldPlayerRenderGifPoster(key: string): boolean;

    shouldPlayerRenderDomGif(key: string): boolean;

    updateElementKey(key: string): void;

    updateActiveThumbnailMedia(key: string, callback: () => void): void;

    removeActiveThumbnailMedia(): void;

    updateIframeSelectState(isSelected: boolean): void;

    changeIframeDisplayState(isSelected: boolean): void;

    resetIframeSelectedState(): void;

    getVideoInstanceByRegion(elementKey: string, region: MediaRegion): VideoType | null;

    setVideoInstancesByRegion(elementKey: string, videoInstance: VideoType, region: MediaRegion): void;

    setAudioInstanceByRegion(elementKey: string, audioInstance: HTMLAudioElement, region: MediaRegion): void;

    getAudioInstanceByRegion(elementKey: string, region: MediaRegion): HTMLAudioElement | null;

    deleteAudioInstanceByRegion(elementKey: string, region: MediaRegion, needCheckPreserveState?: boolean): void;

    resetAllEditorMedia(): void;
    resetAllPlayerMedia(): void;

    // 重置所有不需要保留实例的音视频的状态
    resetAllPlayerMediaIfNeed(): void;

    disposeAllPlayerMediaInstance(needCheckPreserveState: boolean): void;

    disposeAllEditorMediaInstance(): void;

    onInitAnimStyles(elementModel: IElementBaseModel, isExit: boolean): void;

    handlePlayerAnimationStart(elementModel: IElementBaseModel): void;

    handlePlayerAnimationEnd(elementModel: IElementBaseModel, animationModel: AnimationModel): void;

    handleAnimationStart(elementModel: IElementBaseModel): void;

    handleAnimationEnd(elementModel: IElementBaseModel): void;

    handlePlayEnd(): void;

    setPlayerGifAnimationFinishedState(elementModel: IElementBaseModel): void;

    deleteVideoInstanceByRegion(elementKey: string, region: MediaRegion): void;

    playVideoByRegion(elementKey: string, region: MediaRegion): void

    pauseVideoByRegion(elementKey: string, region: MediaRegion)

    shouldVideoRender(elementKey: string, elementId: string, region: MediaRegion): boolean

    playAudioByRegion(elementKey: string, region: MediaRegion): void

    pauseAudioByRegion(elementKey: string, region: MediaRegion)
    resetAudioByRegion(elementKey: string, region: MediaRegion)

    addPlayerMediaElementKey(key: string): void;

    shouldShowMediaElement(elementKey: string, elementId: string, region: MediaRegion): boolean;

    updateElementKeyByRegion(elementKey: string, region: MediaRegion)
    removeElementKeyByRegion(elementKey: string, region: MediaRegion)

    fetchImageAttachmentPoster(model): string | null | Promise<string | null>;

    getPreserveAudioByKey(key: string): PreservedAudio | undefined;

    setPreserveAudioByKey(key: string, preservedAudio: PreservedAudio): void;

    deletePreserveAudioByKey(key: string): void;
    clearPlayerMediaElementKey(): void;
    handlePreserveAudioState(slideIndex: number): void;
    resetAllMobilePreviewMedia(): void;
}
